import type { Section, Claim } from '../../../types/report';
import { resolveBlock } from '../blocks/blockRegistry';

export interface SectionProps {
  section: Section;
  claims: Record<string, Claim>;
}

/** Renders a section's blocks through the block registry. Used both as the
 * body of known section kinds and as the generic fallback for unknown kinds —
 * so an unrecognized kind renders, it never throws. */
function BlocksBody({ section, claims }: SectionProps) {
  return (
    <div className="section-blocks">
      {section.blocks.map((block, i) => {
        const BlockComp = resolveBlock(block.type);
        return <BlockComp key={i} block={block} claims={claims} />;
      })}
    </div>
  );
}

function GenericSection({ section, claims }: SectionProps) {
  // Hide the eyebrow when it just repeats the title (e.g. "EXECUTIVE SUMMARY"
  // above "Executive summary") — case/spacing-insensitive.
  const norm = (s?: string | null) => (s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  const showEyebrow = section.eyebrow && norm(section.eyebrow) !== norm(section.title);
  return (
    <section id={section.id} data-section-kind={section.kind} className="rsection">
      {showEyebrow && <div className="section-eyebrow">{section.eyebrow}</div>}
      <h2 className="section-title">{section.title}</h2>
      <BlocksBody section={section} claims={claims} />
    </section>
  );
}

/** Bare section — the block owns its full chrome (heading, band, gradient), so
 * no rsection wrapper / eyebrow / title. Used by act-divider and marquee. */
function BareSection({ section, claims }: SectionProps) {
  return (
    <section id={section.id} data-section-kind={section.kind}>
      <BlocksBody section={section} claims={claims} />
    </section>
  );
}

// Known section kinds. All render through the shared body (blocks carry the
// distinctive styling); the registry is the extensibility seam — unknown kinds
// fall through to the same generic renderer rather than erroring.
const REGISTRY: Record<string, (props: SectionProps) => JSX.Element> = {
  hero: GenericSection,
  'segments-table': GenericSection,
  'brand-index': GenericSection,
  maxdiff: GenericSection,
  'split-claims': GenericSection,
  demographics: GenericSection,
  triggers: GenericSection,
  channel: GenericSection,
  archetypes: GenericSection,
  prose: GenericSection,
  'two-axis': GenericSection,
  'growth-vectors': GenericSection,
  'focus-brand': GenericSection,
  'act-divider': BareSection,
  marquee: BareSection,
  cover: BareSection,
  'closing-frame': BareSection,
  'question-cards': GenericSection,
  'exec-summary': GenericSection,
  'insight-cards': GenericSection,
  'opp-table': GenericSection,
  'ranked-bars': GenericSection,
  'stat-callout': GenericSection,
  'compare-cards': GenericSection,
};

export function resolveSection(kind: string) {
  return REGISTRY[kind] ?? GenericSection;
}
